"use client";

import { useState } from "react";
import {
  Wifi,
  Tv,
  Music,
  Mic,
  Camera,
  Gamepad,
  Projector,
  Wind,
  Coffee,
  Car,
  Shield,
  Accessibility,
  Monitor,
  Speaker,
  Headphones,
  Lightbulb,
  Palette,
  Sofa,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

interface EquipmentSelectorProps {
  selected: Record<string, boolean>;
  onChange: (equipment: Record<string, boolean>) => void;
}

const EQUIPMENT_CATEGORIES = [
  {
    name: "Technology",
    items: [
      { id: "wifi", label: "Wi-Fi", icon: Wifi },
      { id: "screen", label: "TV/Screen", icon: Tv },
      { id: "projector", label: "Projector", icon: Projector },
      { id: "computer", label: "Computer", icon: Monitor },
      { id: "gaming", label: "Gaming Console", icon: Gamepad },
    ],
  },
  {
    name: "Audio/Visual",
    items: [
      { id: "sound_system", label: "Sound System", icon: Music },
      { id: "microphone", label: "Microphone", icon: Mic },
      { id: "camera", label: "Camera", icon: Camera },
      { id: "speakers", label: "Speakers", icon: Speaker },
      { id: "headphones", label: "Headphones", icon: Headphones },
    ],
  },
  {
    name: "Comfort",
    items: [
      { id: "air_conditioning", label: "Air Conditioning", icon: Wind },
      { id: "heating", label: "Heating", icon: Wind },
      { id: "coffee_machine", label: "Coffee Machine", icon: Coffee },
      { id: "furniture", label: "Comfortable Seating", icon: Sofa },
    ],
  },
  {
    name: "Lighting",
    items: [
      { id: "stage_lighting", label: "Stage Lighting", icon: Lightbulb },
      { id: "mood_lighting", label: "Mood Lighting", icon: Palette },
      { id: "natural_light", label: "Natural Light", icon: Lightbulb },
    ],
  },
  {
    name: "Accessibility",
    items: [
      {
        id: "wheelchair_access",
        label: "Wheelchair Access",
        icon: Accessibility,
      },
      { id: "parking", label: "Parking", icon: Car },
      { id: "security", label: "Security System", icon: Shield },
    ],
  },
];

export default function EquipmentSelector({
  selected,
  onChange,
}: EquipmentSelectorProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [customEquipment, setCustomEquipment] = useState<string[]>([]);
  const [newCustomItem, setNewCustomItem] = useState("");

  const handleToggle = (itemId: string) => {
    onChange({
      ...selected,
      [itemId]: !selected[itemId],
    });
  };

  const handleAddCustom = () => {
    if (newCustomItem.trim() && !customEquipment.includes(newCustomItem)) {
      const customId = `custom_${newCustomItem.toLowerCase().replace(/\s+/g, "_")}`;
      setCustomEquipment([...customEquipment, newCustomItem]);
      handleToggle(customId);
      setNewCustomItem("");
    }
  };

  const filteredCategories = EQUIPMENT_CATEGORIES.map((category) => ({
    ...category,
    items: category.items.filter((item) =>
      item.label.toLowerCase().includes(searchQuery.toLowerCase()),
    ),
  })).filter((category) => category.items.length > 0);

  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <div className="space-y-4">
      {/* Search */}
      <div>
        <Input
          placeholder="Search equipment..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full"
        />
      </div>

      {/* Selected Count */}
      <div className="text-sm text-muted-foreground">
        {selectedCount} item{selectedCount !== 1 ? "s" : ""} selected
      </div>

      {/* Equipment Categories */}
      <Accordion type="multiple" className="w-full">
        {filteredCategories.map((category) => (
          <AccordionItem key={category.name} value={category.name}>
            <AccordionTrigger className="text-sm font-medium">
              {category.name}
              <span className="ml-2 text-muted-foreground">
                ({category.items.filter((item) => selected[item.id]).length}/
                {category.items.length})
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
                {category.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.id} className="flex items-center space-x-3">
                      <Checkbox
                        id={item.id}
                        checked={selected[item.id] || false}
                        onCheckedChange={() => handleToggle(item.id)}
                      />
                      <Label
                        htmlFor={item.id}
                        className="flex items-center gap-2 cursor-pointer font-normal"
                      >
                        <Icon className="h-4 w-4 text-muted-foreground" />
                        {item.label}
                      </Label>
                    </div>
                  );
                })}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}

        {/* Custom Equipment */}
        <AccordionItem value="custom">
          <AccordionTrigger className="text-sm font-medium">
            Custom Equipment
            <span className="ml-2 text-muted-foreground">
              ({customEquipment.length})
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 pt-2">
              {customEquipment.map((item) => {
                const customId = `custom_${item.toLowerCase().replace(/\s+/g, "_")}`;
                return (
                  <div key={customId} className="flex items-center space-x-3">
                    <Checkbox
                      id={customId}
                      checked={selected[customId] || false}
                      onCheckedChange={() => handleToggle(customId)}
                    />
                    <Label
                      htmlFor={customId}
                      className="cursor-pointer font-normal"
                    >
                      {item}
                    </Label>
                  </div>
                );
              })}

              <div className="flex gap-2 mt-3">
                <Input
                  placeholder="Add custom equipment..."
                  value={newCustomItem}
                  onChange={(e) => setNewCustomItem(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleAddCustom()}
                  className="flex-1"
                />
                <button
                  type="button"
                  onClick={handleAddCustom}
                  className="px-3 py-2 bg-primary text-primary-foreground rounded-md text-sm hover:bg-primary/90"
                >
                  Add
                </button>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
